import React, { useEffect, useState, useCallback } from 'react';
import { Printer, RefreshCw, Trash2, AlertTriangle, Volume2, VolumeX } from 'lucide-react';
import { Button } from '../ui/button';
import { Badge } from '../ui/badge';
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from '../ui/popover';
import { ScrollArea } from '../ui/scroll-area';
import { toast } from 'sonner';
import {
  getQueue,
  subscribe,
  dequeue,
  clearQueue,
  retryAllNow,
  startBackgroundRetryLoop,
} from '../../utils/printQueue';
import { getSavedPrinterName } from '../../utils/qzPrinter';
import {
  isPrintFailAlertMuted,
  setPrintFailAlertMuted,
  playPrintFailAlert,
} from '../../utils/soundAlert';

const formatRelative = (ts) => {
  if (!ts) return '';
  const diff = Math.max(0, Date.now() - ts);
  const s = Math.floor(diff / 1000);
  if (s < 60) return `${s}s ago`;
  const m = Math.floor(s / 60);
  if (m < 60) return `${m}m ago`;
  const h = Math.floor(m / 60);
  if (h < 24) return `${h}h ago`;
  return `${Math.floor(h / 24)}d ago`;
};

export const PrintQueueBadge = ({ printerSettings }) => {
  const [queue, setQueue] = useState(getQueue());
  const [retrying, setRetrying] = useState(false);
  const [open, setOpen] = useState(false);
  const [muted, setMuted] = useState(isPrintFailAlertMuted());

  // Keep the badge live-synced with the store + start background loop.
  useEffect(() => {
    const unsub = subscribe(setQueue);
    startBackgroundRetryLoop(() =>
      printerSettings?.printer_name || getSavedPrinterName()
    );
    // Cross-tab sync via storage events
    const onStorage = (e) => {
      if (e.key === 'qz_print_queue_v1') setQueue(getQueue());
    };
    window.addEventListener('storage', onStorage);
    return () => {
      unsub();
      window.removeEventListener('storage', onStorage);
    };
  }, [printerSettings?.printer_name]);

  // Force a re-render every 30s so "3m ago" stays fresh while popover open.
  useEffect(() => {
    if (!open) return;
    const t = setInterval(() => setQueue((q) => [...q]), 30 * 1000);
    return () => clearInterval(t);
  }, [open]);

  const handleRetryAll = useCallback(async () => {
    const name = printerSettings?.printer_name || getSavedPrinterName();
    if (!name) {
      toast.error('No printer selected. Pick one in Printer Settings first.');
      return;
    }
    setRetrying(true);
    try {
      const { attempted, succeeded } = await retryAllNow(name);
      if (attempted === 0) {
        toast.info('Print queue is empty.');
      } else if (succeeded === attempted) {
        toast.success(`Reprinted ${succeeded} of ${attempted} queued receipt${attempted === 1 ? '' : 's'}.`);
      } else if (succeeded === 0) {
        toast.error('Still failing — is QZ Tray running and the printer online?');
      } else {
        toast.warning(`Reprinted ${succeeded} of ${attempted}. Some are still failing.`);
      }
    } finally {
      setRetrying(false);
    }
  }, [printerSettings?.printer_name]);

  const handleClear = () => {
    clearQueue();
    toast.info('Print queue cleared.');
  };

  const toggleMute = () => {
    const next = !muted;
    setPrintFailAlertMuted(next);
    setMuted(next);
    if (!next) {
      // Preview the sound so staff know what to listen for.
      playPrintFailAlert();
      toast.success('Print-fail sound alert enabled');
    } else {
      toast.info('Print-fail sound alert muted');
    }
  };

  const count = queue.length;
  if (count === 0) return null; // hide badge when queue is empty

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <Button
          variant="outline"
          size="sm"
          className="relative rounded-full gap-2 border-amber-400 text-amber-800 hover:bg-amber-50"
          data-testid="print-queue-badge"
        >
          <Printer className="h-4 w-4" />
          <span className="hidden sm:inline">Print queue</span>
          <Badge
            className="ml-1 bg-red-600 hover:bg-red-600 text-white text-[10px] px-1.5 h-5 rounded-full"
            data-testid="print-queue-count"
          >
            {count}
          </Badge>
        </Button>
      </PopoverTrigger>
      <PopoverContent align="end" className="w-96 p-0" data-testid="print-queue-popover">
        <div className="p-4 border-b bg-amber-50/60">
          <div className="flex items-center gap-2 mb-1">
            <AlertTriangle className="h-4 w-4 text-amber-700" />
            <div className="text-sm font-semibold text-amber-900">
              {count} unprinted receipt{count === 1 ? '' : 's'}
            </div>
            <button
              type="button"
              onClick={toggleMute}
              title={muted ? 'Unmute print-fail sound alert' : 'Mute print-fail sound alert'}
              data-testid="print-fail-mute-toggle"
              className="ml-auto inline-flex items-center gap-1 rounded-full border border-amber-300 bg-white/70 px-2 py-0.5 text-[11px] font-medium text-amber-800 hover:bg-white"
            >
              {muted ? <VolumeX className="h-3 w-3" /> : <Volume2 className="h-3 w-3" />}
              {muted ? 'Muted' : 'Sound on'}
            </button>
          </div>
          <p className="text-xs text-amber-800/80">
            The queue retries every 5s in the background. You can retry now or clear items you've printed manually.
          </p>
        </div>

        <ScrollArea className="max-h-72">
          <ul className="divide-y">
            {queue.map((item) => {
              const order = item.order || {};
              const dayNum = order.daily_order_number || '';
              const type = (order.order_type || '').toUpperCase();
              return (
                <li key={item.orderId} className="p-3 flex items-start justify-between gap-3">
                  <div className="min-w-0 flex-1">
                    <div className="text-sm font-medium">
                      {dayNum ? `#${dayNum} · ` : ''}{order.customer_name || 'Order'}
                    </div>
                    <div className="text-xs text-muted-foreground truncate">
                      {type} · £{Number(order.total_amount || 0).toFixed(2)} · {item.attempts} attempt{item.attempts === 1 ? '' : 's'} · {formatRelative(item.lastAttemptAt)}
                    </div>
                    {item.lastError && (
                      <div className="text-[11px] text-red-600 truncate mt-0.5" title={item.lastError}>
                        {item.lastError}
                      </div>
                    )}
                  </div>
                  <Button
                    variant="ghost"
                    size="icon"
                    className="h-7 w-7 shrink-0"
                    onClick={() => dequeue(item.orderId)}
                    title="Remove from queue"
                    data-testid={`print-queue-remove-${item.orderId}`}
                  >
                    <Trash2 className="h-3.5 w-3.5 text-muted-foreground" />
                  </Button>
                </li>
              );
            })}
          </ul>
        </ScrollArea>

        <div className="p-3 border-t flex items-center justify-between gap-2 bg-muted/30">
          <Button
            variant="ghost"
            size="sm"
            onClick={handleClear}
            className="text-muted-foreground hover:text-red-600"
            data-testid="print-queue-clear-btn"
          >
            Clear all
          </Button>
          <Button
            size="sm"
            onClick={handleRetryAll}
            disabled={retrying}
            className="bg-red-600 hover:bg-red-700 text-white gap-2"
            data-testid="print-queue-retry-btn"
          >
            <RefreshCw className={`h-3.5 w-3.5 ${retrying ? 'animate-spin' : ''}`} />
            {retrying ? 'Retrying…' : 'Retry all'}
          </Button>
        </div>
      </PopoverContent>
    </Popover>
  );
};

export default PrintQueueBadge;
